import React, { useState, useEffect } from 'react';

import moment from 'moment';

import DatepickerRange from '../../../../components/Inputs/DatepickerRange';
import DatepickerMui from '../../../../components/Inputs/DatepickerMui';
import Select from '../../../../components/Inputs/Select';
import DataTableMui from '../../../../components/DataTableMui';
import Tag from '../../../../components/Tag';
import PageLoader from '../../../../components/PageLoader';
import LinkBase64 from '../../../../components/LinkBase64';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';

import options from './options';
import filterSchema from './schema';

import { downloadFile } from '../../../../utils';

import { buscaPorArquivo, buscaRelatorio, buscaDevolutiva } from '../../../../services';

import { useGlobal } from '../../../../contexts/Global/context';

import * as S from './styles';
import Input from '../../../../components/Inputs/Input';

const ConsultarEnvios = ({ graphFilter }) => {
    const { dataActiveContract, contrastTheme } = useGlobal();

    const DATE_FORMAT_GRID = 'DD/MM/YYYY [às] H[h]mm';
    const FORMAT_API = 'MM/DD/YYYY H:mm:ss';

    const [filterValues, setFilterValues] = useState({
        ...filterSchema,
        movementDate: {
            ...filterSchema.movementDate,
            value: graphFilter?.dateRange,
        },
        movementStatus: {
            ...filterSchema.movementStatus,
            value: graphFilter?.statusGroup.toString(),
        },
        nomeArquivo: {
            ...filterSchema.nomeArquivo,
            value: graphFilter?.nomeArquivo.toString(),
        },
    });
    const [movimentacoes, setMovimentacoes] = useState([]);

    const [loading, setLoading] = useState(true);
    const [alreadySearched, setAlreadySearched] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [dataTable, setDataTable] = useState([]);
    const [showModal, setShowModal] = useState(false);

    const dataByStatus = {
        devolutiva: {
            label: 'Arquivo de devolutiva',
            endpoint: async (seqArquivo) => (await buscaDevolutiva(seqArquivo)) || {},
        },
        relatorio: {
            label: 'Relatório de erros',
            endpoint: async (seqArquivo) => (await buscaRelatorio(seqArquivo)) || {},
        },
    };

    function checkAndApplyFilters(filter, moviments) {
        const hasSomeFilter = Object.values(filter).filter((filterValue) => filterValue.value).length > 0;

        if (hasSomeFilter) {
            const dataFiltered = moviments.filter(({ referencia, dataImportacao, statusCodigoSituacao }) => {
                const { movementRef, movementStatus, movementDate } = filter;

                const statusList = options.statusDetail
                    .filter((status) => +status.grupo === +movementStatus.value)
                    .map((status) => status.codigo);

                const [dateStart, dateEnd] = movementDate.value || ['', ''];

                return (
                    (movementRef.value
                        ? moment(movementRef.value).format('MM/YYYY') ===
                        moment(referencia, FORMAT_API).format('MM/YYYY')
                        : true) &&
                    (movementStatus.value ? statusList.includes(+statusCodigoSituacao) : true) &&
                    (!!dateStart && !!dateEnd
                        ? moment(moment(dataImportacao).format('YYYY-MM-DD')).isBetween(
                            moment(dateStart).format('YYYY-MM-DD'),
                            moment(dateEnd).format('YYYY-MM-DD'),
                            undefined, // Parametro de ano, dia, nao utlizado
                            '[]' // considerar dataInicio igual ao valor comparado, e nao apenas maior
                        )
                        : true)
                );
            });

            setDataTable(dataFiltered);

            return;
        }

        setDataTable(moviments);
    }

    function createStatusTag({ nome, cor }) {
        return (
            <Tag tooltip={nome} tagColor={contrastTheme ? '#FFFFFF' : cor}>
                {nome}
            </Tag>
        );
    }

    async function handleDownloadFile({ sequencial, typeDonwload }) {
        setPageLoading(true);

        const { nome, base64 } = await dataByStatus[typeDonwload].endpoint(sequencial);

        setPageLoading(false);

        if (!nome || !base64) {
            setShowModal(true);
            return;
        }

        const extension = nome.substring(nome.lastIndexOf('.') + 1);

        let header = '';

        if (extension === 'pdf') {
            header = 'data:application/pdf;base64,';
        }

        if (extension?.toLowerCase() === 'txt') {
            header = 'data:text/plain;base64,';
        }

        const href = `${header}${base64}`;

        downloadFile(nome, href);
    }

    function createStatusField(dataStatusDetail, dataStatusGroup, dataRow) {
        return (
            <S.StatusField>
                <S.StatusTag>{dataStatusDetail.grupo ? createStatusTag(dataStatusGroup) : ''}</S.StatusTag>
                {dataStatusGroup.codigo === 3 && (
                    <S.Description>{`${dataRow.qtdRegistrosRecusados} erro(s) encontrado(s)`}</S.Description>
                )}
            </S.StatusField>
        );
    }

    function createLinkFile({ sequencial, grupo }) {
        if (grupo === 2) {
            return (
                <S.GroupLinks>
                    <LinkBase64 onClick={() => handleDownloadFile({ sequencial, typeDonwload: 'devolutiva' })}>
                        <S.TextTable>{dataByStatus.devolutiva.label}</S.TextTable>
                    </LinkBase64>
                    <LinkBase64 onClick={() => handleDownloadFile({ sequencial, typeDonwload: 'relatorio' })}>
                        <S.TextTable>{dataByStatus.relatorio.label}</S.TextTable>
                    </LinkBase64>
                </S.GroupLinks>
            );
        }

        if (grupo === 3) {
            return (
                <LinkBase64 onClick={() => handleDownloadFile({ sequencial, typeDonwload: 'relatorio' })}>
                    <S.TextTable>{dataByStatus.relatorio.label}</S.TextTable>
                </LinkBase64>
            );
        }

        return <>-</>;
    }

    const columnsHead = [
        {
            key: 'nomeArquivo',
            selector: (row) => row.nomeArquivo || '-',
            name: 'Nome do arquivo',
            sortable: true,
            direction: 'center',
            customStyles: () => ({ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }),
        },
        {
            key: 'referenciaFormatted',
            selector: (row) => row.referenciaFormatted || '-',
            name: 'Referência',
            sortable: true,
            direction: 'center',
            customStyles: () => ({ width: '150px' }),
        },
        {
            key: 'dataImportacaoFormatted',
            selector: (row) => row.dataImportacaoFormatted || '-',
            name: 'Data de importação',
            sortable: true,
            direction: 'center',
            customStyles: () => ({ width: '230px' }),
        },
        {
            key: 'statusID',
            selector: (row) => row.status || '-',
            name: 'Status',
            sortable: true,
            direction: 'left',
            customStyles: () => ({ width: '220px' }),
        },
        {
            key: 'type',
            selector: () => (filterValues.typeFile.value === 'C' ? 'Por contrato' : 'Por grupo de contrato'),
            name: 'Tipo movimentação',
            sortable: true,
            direction: 'left',
        },
        {
            key: 'file',
            selector: (row) => row.file || '-',
            name: '',
            sortable: false,
            direction: 'center',
            customStyles: () => ({ cursor: 'pointer', textAlign: 'center', width: '250px' }),
        },
    ];

    async function loadData(searchType) {
        setLoading(true);

        let params = {}

        if (typeof searchType !== "undefined") {
            params = {
                numeroContrato: dataActiveContract.numero,
                situacaoContrato: dataActiveContract.situacao === 'A' ? 'Ativo' : 'Inativo',
                mesAnoReferencia: searchType.movementRef.value !== null && searchType.movementRef.value !== undefined ? `${searchType.movementRef.value.split("-")[1]}-${searchType.movementRef.value.split("-")[0]}` : "",
                dataInicioPeriodo: searchType.movementDate.value !== null && searchType.movementDate.value !== undefined ? `${searchType.movementDate.value[0].split("-")[2].split("T")[0]}-${searchType.movementDate.value[0].split("-")[1]}-${searchType.movementDate.value[0].split("-")[0]}` : "",
                dataFimPeriodo: searchType.movementDate.value !== null && searchType.movementDate.value !== undefined ? `${searchType.movementDate.value[1].split("-")[2].split("T")[0]}-${searchType.movementDate.value[1].split("-")[1]}-${searchType.movementDate.value[1].split("-")[0]}` : "",
                nomeArquivo: searchType.nomeArquivo.value !== undefined && searchType.nomeArquivo.value !== null ? searchType.nomeArquivo.value : ""
            };

            if (searchType.typeFile.value !== undefined && searchType.typeFile.value === 'G') {
                delete params.numeroContrato;
                params.codigoGrupoEmpresa = dataActiveContract.codGrupoEmpresa;
            }
        } else {
            params = {
                numeroContrato: dataActiveContract.numero,
                situacaoContrato: dataActiveContract.situacao === 'A' ? 'Ativo' : 'Inativo',
            };
        }

        if (searchType === 'G') {
            delete params.numeroContrato;
            params.codigoGrupoEmpresa = dataActiveContract.codGrupoEmpresa;
        }

        const urlParams = new URLSearchParams(params).toString();

        const response = (await buscaPorArquivo(urlParams)) || [];

        const treatedResponse = response.map((row) => {
            const dataStatusDetail = options.statusDetail.find(
                (status) => +status.codigo === +row.statusCodigoSituacao
            );

            const dataStatusGroup = options.statusGroup.find((status) => status.codigo === dataStatusDetail?.grupo);
            return {
                ...row,
                dataImportacaoFormatted: row.dataImportacao && moment(row.dataImportacao).format(DATE_FORMAT_GRID),
                referenciaFormatted: row.referencia && moment(row.referencia, FORMAT_API).format('MM/YYYY'),
                status: createStatusField(dataStatusDetail, dataStatusGroup, row),
                statusGroup: dataStatusGroup,
                file: createLinkFile({ sequencial: row.numSequencialArquivo, grupo: dataStatusDetail.grupo }),
            };
        });

        setMovimentacoes(treatedResponse);
        setDataTable(treatedResponse);

        setLoading(false);
        setAlreadySearched(true);

        if (graphFilter) checkAndApplyFilters(filterValues, treatedResponse);
    }

    async function handleChangeFilter(event) {
        const { id, value } = event;

        const filter = {
            ...filterValues,
            [id]: { ...filterValues[id], value },
        };

        const isTypeFile = id === 'typeFile';

        // if (isTypeFile && value && value !== filterValues.typeFile.value) {
        //     await loadData(value);
        // }

        if (!isTypeFile || value) setFilterValues({ ...filter });

        if (!isTypeFile) checkAndApplyFilters(filter, movimentacoes);
    }

    const handleSubmit = async () => {
        const filter = {
            ...filterValues,
        };

        // eslint-disable-next-line no-console
        console.log(filter)

        await loadData(filter);
    }

    useEffect(() => {
        loadData();
    }, []);

    return (
        <>
            <S.FiltersLabel>Filtrar por: </S.FiltersLabel>
            <S.FormGroup>
                <Input
                    field={{ ...filterValues.nomeArquivo }}
                    onChange={(event) => handleChangeFilter(event)}
                />

                <Select
                    field={{ ...filterValues.typeFile, options: options.typeFile }}
                    onChange={(event) => handleChangeFilter(event)}
                />

                <DatepickerMui
                    field={{ ...filterValues.movementRef, icon: { name: 'search', type: 'round' } }}
                    openTo="month"
                    format="MM/yyyy"
                    views={['month']}
                    minDate="1968/01"
                    onChange={(event) => handleChangeFilter(event)}
                />

            </S.FormGroup>

            <S.FormGroup>
                <DatepickerRange
                    field={{ ...filterValues.movementDate }}
                    onChange={(event) => handleChangeFilter(event)}
                />

                <Select
                    field={{ ...filterValues.movementStatus, options: options.statusGroup }}
                    onChange={(event) => handleChangeFilter(event)}
                />

                <Button type="primary" onClick={handleSubmit}>
                    Pesquisar
                </Button>
            </S.FormGroup>

            <div>
                <DataTableMui
                    clickableRow={false}
                    data={dataTable}
                    emptyMessage="Não encontramos resultados. Você pode fazer uma nova pesquisa."
                    loading={loading}
                    columns={columnsHead}
                    alreadySearched={alreadySearched}
                />
            </div>
            <Modal
                title="Atenção"
                subtitle="Este arquivo ainda não foi disponibilizado"
                visible={showModal}
                onCancel={() => setShowModal(false)}
                footer={null}
            >
                <Button type="primary" onClick={() => setShowModal(false)}>
                    Ok
                </Button>
            </Modal>

            <PageLoader loading={pageLoading} />
        </>
    );
};

export default ConsultarEnvios;
